import { useShowNotification } from '@inflearn/ds-react-next';
import { MonitoringService } from '@inflearn/monitoring';
import { useTranslation } from 'next-i18next';
import qs from 'query-string';

import {
  API_ENDPOINT,
  fetchClientApiV1MarketingPartnerLinksCreatePreview,
} from '../../resources/@types/courseSchema';
import { ApiError } from '../../resources/ApiError';
import { useMarketingPartnerLinksCreateMutation } from '../mutations/useMarketingPartnerLinksCreateMutation';
import useClientAccountProfileQuery from '../queries/useClientAccountProfileQuery';
import { useCopy } from './useCopy';

const getPromotionalLinkWithGuide = (label: string, link: string) => `${label}

${link}`;

export const useCopyMarketingPartnerLink = () => {
  const { t } = useTranslation();
  const { showNotification } = useShowNotification();
  const { data: profile } = useClientAccountProfileQuery();

  const { copyAsync } = useCopy({
    onSuccess: () => {
      showNotification({
        title: t('홍보 링크 복사 완료'),
        type: 'success',
      });
    },
    onError: (error: unknown) => {
      if (error instanceof Error || ApiError.isApiError(error)) {
        MonitoringService.getInstance().datadog.addError('홍보 링크 복사 실패', {
          error,
        });
        return;
      }

      showNotification({
        title: t('홍보 링크 복사 실패'),
        type: 'error',
      });
    },
  });

  const { mutateAsync: createMarketingPartnerLink } = useMarketingPartnerLinksCreateMutation({
    options: {
      onError: (error: unknown, { courseId }) => {
        MonitoringService.getInstance().datadog.addError('홍보 링크 생성 실패', {
          courseId,
          userId: profile?.id,
          error,
        });
      },
    },
  });

  const handleCopyMarketingPartnerLink = (courseId: number) => {
    copyAsync(
      fetchClientApiV1MarketingPartnerLinksCreatePreview(
        qs.stringifyUrl({
          url: API_ENDPOINT.CLIENT_V1_MARKETING_PARTNER_LINKS_CREATE_PREVIEW,
          query: {
            courseId,
          },
        }),
      ).then(({ data }) => {
        if (!data.canCreate) {
          showNotification({
            title: t('홍보 대상이 아닙니다.'),
            type: 'error',
          });

          return Promise.reject(new Error('UNAVAILABLE'));
        }

        return createMarketingPartnerLink({ courseId }).then(({ data }) =>
          Promise.resolve(
            getPromotionalLinkWithGuide(
              t('이 링크를 통해 구매하시면 제가 수익을 받을 수 있어요. 🤗'),
              data.link,
            ),
          ),
        );
      }),
    );
  };

  return { copyMarketingPartnerLink: handleCopyMarketingPartnerLink };
};
